import {
  FictionalRule,
  FictionalRuleCreatePayload,
  FictionalRuleUpdatePayload,
  FictionalRulesQueryParams,
} from "../../models/rulesFictional";
import { API_ENDPOINTS } from "../../utils/api-service";
import { api } from "./api";

const fictionalRuleTag = (id: number) => ({
  type: "rulesFictional" as const,
  id,
});

const fictionalRuleListTag = { type: "rulesFictional" as const, id: "LIST" };

const toBoolean = (value: unknown): boolean => {
  if (value === false || value === 0 || value === "0" || value === "false") {
    return false;
  }

  return value === true || value === 1 || value === "1" || value === "true";
};

const unwrapApiData = (response: unknown): unknown => {
  if (typeof response !== "object" || response === null) {
    return response;
  }

  const record = response as Record<string, unknown>;
  if (record.success === true && record.data !== undefined) {
    return record.data;
  }

  return response;
};

const toNullableInt = (value: unknown): number | null => {
  if (value == null || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

const toInt = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
};

const mapFictionalRule = (raw: unknown): FictionalRule => {
  const row = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const eventSubTypeRaw = row.eventSubTypeId ?? row.event_sub_type_id;

  return {
    id: toInt(row.id),
    eventTypeId: toInt(row.eventTypeId ?? row.event_type_id),
    eventSubTypeId: toNullableInt(eventSubTypeRaw),
    tenantId:
      row.tenantId == null && row.tenant_id == null
        ? null
        : String(row.tenantId ?? row.tenant_id),
    active: toBoolean(row.active),
    eventTypeName:
      row.eventTypeName == null && row.event_type_name == null
        ? undefined
        : String(row.eventTypeName ?? row.event_type_name ?? ""),
    eventSubTypeName:
      row.eventSubTypeName == null && row.event_sub_type_name == null
        ? null
        : String(row.eventSubTypeName ?? row.event_sub_type_name ?? ""),
    tenantName:
      row.tenantName == null && row.tenant_name == null
        ? null
        : String(row.tenantName ?? row.tenant_name ?? ""),
    initialFictional: toInt(row.initialFictional ?? row.initial_fictional),
    displacementRate: toInt(row.displacementRate ?? row.displacement_rate, 1),
    minFictional: toInt(row.minFictional ?? row.min_fictional),
    createdAt:
      row.createdAt == null && row.created_at == null
        ? undefined
        : String(row.createdAt ?? row.created_at),
  };
};

const normalizeListResponse = (response: unknown): unknown[] => {
  const unwrapped = unwrapApiData(response);
  return Array.isArray(unwrapped) ? unwrapped : [];
};

const normalizeFictionalRulesQueryArgs = (
  params: FictionalRulesQueryParams = {}
): Record<string, string | number> => {
  const normalized: Record<string, string | number> = {};

  if (params.eventTypeId != null) {
    normalized.event_type_id = params.eventTypeId;
  }
  if (params.tenantId != null) {
    normalized.tenant_id = params.tenantId;
  }

  return normalized;
};

const listProvidesTags = (result: FictionalRule[] | undefined) =>
  result
    ? [...result.map(({ id }) => fictionalRuleTag(id)), fictionalRuleListTag]
    : [fictionalRuleListTag];

type EbApiRootState = {
  ebAPI?: {
    queries?: Record<
      string,
      { endpointName?: string; originalArgs?: FictionalRulesQueryParams }
    >;
  };
};

const patchFictionalRulesListCaches = (
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
  updater: (draft: FictionalRule[]) => void
): Array<{ undo: () => void }> => {
  const patchResults: Array<{ undo: () => void }> = [];
  const rootState = getState() as EbApiRootState;

  for (const query of Object.values(rootState.ebAPI?.queries ?? {})) {
    if (query.endpointName !== "getFictionalRules") {
      continue;
    }

    const cacheArgs = (query.originalArgs ?? {}) as FictionalRulesQueryParams;
    const cachedList = rulesFictionalApi.endpoints.getFictionalRules.select(cacheArgs)(
      rootState as never
    );
    if (!cachedList.data) {
      continue;
    }

    const patch = dispatch(
      rulesFictionalApi.util.updateQueryData("getFictionalRules", cacheArgs, updater)
    ) as { undo?: () => void };

    if (patch?.undo) {
      patchResults.push({ undo: patch.undo });
    }
  }

  return patchResults;
};

const toRuleBody = (body: FictionalRuleCreatePayload) => ({
  eventTypeId: toInt(body.eventTypeId),
  eventSubTypeId: toNullableInt(body.eventSubTypeId),
  tenantId: body.tenantId,
  initialFictional: toInt(body.initialFictional),
  displacementRate: toInt(body.displacementRate, 1),
  minFictional: toInt(body.minFictional),
  active: toBoolean(body.active),
});

export const rulesFictionalApi = api.injectEndpoints({
  endpoints: (build) => ({
    getFictionalRules: build.query<FictionalRule[], FictionalRulesQueryParams>({
      query: (params) => ({
        url: API_ENDPOINTS.RULES.FICTIONAL.BASE,
        params: normalizeFictionalRulesQueryArgs(params),
      }),
      transformResponse: (response: unknown) =>
        normalizeListResponse(response).map(mapFictionalRule),
      providesTags: (result) => listProvidesTags(result),
    }),

    createFictionalRule: build.mutation<FictionalRule, FictionalRuleCreatePayload>({
      query: (body) => ({
        url: API_ENDPOINTS.RULES.FICTIONAL.BASE,
        method: "POST",
        body: toRuleBody(body),
      }),
      transformResponse: (response: unknown) => mapFictionalRule(unwrapApiData(response)),
      invalidatesTags: [fictionalRuleListTag],
    }),

    updateFictionalRule: build.mutation<FictionalRule, FictionalRuleUpdatePayload>({
      query: (body) => ({
        url: API_ENDPOINTS.RULES.FICTIONAL.BASE,
        method: "PUT",
        body: {
          id: toInt(body.id),
          ...toRuleBody(body),
        },
      }),
      transformResponse: (response: unknown) => mapFictionalRule(unwrapApiData(response)),
      invalidatesTags: (_result, _error, { id }) => [fictionalRuleTag(id), fictionalRuleListTag],
    }),

    toggleFictionalRuleActive: build.mutation<FictionalRule, number>({
      query: (id) => ({
        url: API_ENDPOINTS.RULES.FICTIONAL.TOGGLE(id),
        method: "PATCH",
      }),
      transformResponse: (response: unknown) => mapFictionalRule(unwrapApiData(response)),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const optimisticPatches = patchFictionalRulesListCaches(dispatch, getState, (draft) => {
          const row = draft.find((item) => item.id === id);
          if (row) {
            row.active = !row.active;
          }
        });

        try {
          const { data } = await queryFulfilled;
          patchFictionalRulesListCaches(dispatch, getState, (draft) => {
            const row = draft.find((item) => item.id === id);
            if (row) {
              Object.assign(row, data);
            }
          });
        } catch {
          optimisticPatches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: (_result, _error, id) => [fictionalRuleTag(id), fictionalRuleListTag],
    }),
  }),
});

export const {
  useGetFictionalRulesQuery,
  useCreateFictionalRuleMutation,
  useUpdateFictionalRuleMutation,
  useToggleFictionalRuleActiveMutation,
} = rulesFictionalApi;
